import { apiClient } from './api';
import { Application, CreateApplicationData, UpdateApplicationData, PaginatedResponse, PaginationParams } from '../types';

interface InterviewData {
  type: 'phone' | 'video' | 'in-person' | 'technical' | 'behavioral' | 'case_study' | 'panel';
  date: string;
  duration?: number;
  interviewer?: {
    name?: string;
    title?: string;
    email?: string;
  };
  notes?: string;
}

export const applicationsService = {
  // Get all applications
  getApplications: async (params: PaginationParams = {}): Promise<PaginatedResponse<Application>> => {
    const response = await apiClient.get('/api/applications', { params });
    return {
      data: response.applications,
      pagination: response.pagination
    };
  },

  // Get application by ID
  getApplication: async (id: string): Promise<Application> => {
    const response = await apiClient.get(`/api/applications/${id}`);
    return response.application;
  },

  // Create new application
  createApplication: async (applicationData: CreateApplicationData): Promise<Application> => {
    const response = await apiClient.post('/api/applications', applicationData);
    return response.application;
  },

  // Update application
  updateApplication: async (id: string, applicationData: UpdateApplicationData): Promise<Application> => {
    const response = await apiClient.put(`/api/applications/${id}`, applicationData);
    return response.application;
  },

  // Update application status
  updateApplicationStatus: async (id: string, status: string, notes?: string): Promise<Application> => {
    const response = await apiClient.patch(`/api/applications/${id}/status`, { status, notes });
    return response.application;
  },

  // Delete application
  deleteApplication: async (id: string): Promise<{ message: string }> => {
    const response = await apiClient.delete(`/api/applications/${id}`);
    return { message: response.message || 'Application deleted successfully' };
  },

  // Get applications by status
  getApplicationsByStatus: async (status: string, params: PaginationParams = {}): Promise<PaginatedResponse<Application>> => {
    const response = await apiClient.get(`/api/applications/status/${status}`, { params });
    return {
      data: response.applications,
      pagination: response.pagination
    };
  },

  // Get applications with upcoming follow-ups
  getUpcomingFollowUps: async (params: PaginationParams = {}): Promise<Application[]> => {
    const response = await apiClient.get('/api/applications/followups/upcoming', { params });
    return response.applications;
  },

  // Add interview to application
  addInterview: async (id: string, interviewData: InterviewData): Promise<Application> => {
    const response = await apiClient.post(`/api/applications/${id}/interviews`, interviewData);
    return response.application;
  },

  // Get application statistics
  getApplicationStats: async (): Promise<any> => {
    const response = await apiClient.get('/api/applications/stats/summary');
    return response.stats;
  },

  // Get applications by company
  getApplicationsByCompany: async (companyId: string, params: PaginationParams = {}): Promise<PaginatedResponse<Application>> => {
    const response = await apiClient.get('/api/applications', { 
      params: { ...params, company: companyId } 
    });
    return {
      data: response.applications,
      pagination: response.pagination
    };
  },

  // Get applications by referral contact
  getApplicationsByReferral: async (contactId: string, params: PaginationParams = {}): Promise<PaginatedResponse<Application>> => {
    const response = await apiClient.get('/api/applications', { params });
    return {
      data: response.applications.filter((a: { referralContact: { _id: string; }; }) => a.referralContact?._id === contactId),
      pagination: response.pagination
    };
  }
}; 